import React, { useRef, useState, useMemo } from 'react';
import ReactECharts, { EChartsInstance } from 'echarts-for-react';
import { DataZoomComponentOption, EChartsOption, LineSeriesOption, SeriesOption, YAXisComponentOption } from 'echarts';

// Enums
import CycleMeasurement from '../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../enums/cycle-measurement-symbol';

// Utils
import getTempByMeasurement from '../../utils/getTempByMeasurement';
import { generateTimeAxisValues } from '../../utils/chart-utils';
import { getDefaultDataZoom, getLttbData } from './helpers';
import {
  ComponentProps,
  Coordinate,
  CycleComponents,
  DataEvent,
  ParameterGetOptionFn,
  ParameterGetVisibleDataFn,
} from './types';
import {
  CHARACTER_SEPARATOR_DEVICE_NAME,
  CHARACTER_SEPARATOR_IOT_KEY_NAME,
  CHART_INDENTS,
  CLASS_NAME_SPACE,
  DATA_ZOOM_ID,
  DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS,
  DEVICE_NAME,
  SET_POINT_NAME,
  NOTE_ID,
  AXIS_CONFIG,
} from './constants';
import { DateFormatter } from '../../utils/date-formatter';
import { DateFormats } from '../../enums/date-formats';
import ListLegendsCycle from '../ListLegendsCycle/ListLegendsCycle';
import applyYAxisPadding from '../../utils/applyYAxisPadding';
import { useTranslation } from 'react-i18next';
import getColorByDeviceIndex from '../../utils/getColorByDeviceIndex';
import { TIME } from '../../constants/time.constants';

export function useZoomTrigger() {
  const lastTriggerRef = useRef<number | null>(null);

  const shouldRefetch = (start: number, end: number): boolean => {
    const zoomLevel = 100 - (end - start);

    const currentStep = Math.floor(zoomLevel / 10) * 10;

    if (lastTriggerRef.current === null || currentStep !== lastTriggerRef.current) {
      lastTriggerRef.current = currentStep;
      return true;
    }

    return false;
  };

  return shouldRefetch;
}

export const StaticChart: React.FC<ComponentProps> = ({
  cycleComponents,
  createNote,
  editNote,
  measurement,
  isNoteView,
  chartPreference,
  cycleControllersName,
  nameDevices,
  chartRef,
  excludedChannelNames,
  cycleControllers,
  colorsDevices,
  generateNoteName,
}) => {
  const { t } = useTranslation();
  const shouldRefetch = useZoomTrigger();

  const isLoading = useMemo(() => {
    return !(cycleComponents && cycleComponents.source.length > 0);
  }, [cycleComponents]);

  const channelsToHide = excludedChannelNames?.flatMap((channelNames) => {
    const controllerName =
      cycleControllers?.find((item) => item.controller.id === channelNames.controllerId)?.controller.serial_number ||
      null;
    if (!controllerName) return [];

    const channelList = [...channelNames.excludeChannels, ...channelNames.excludeThermocouples];
    return channelList.map((channel) => `${controllerName}#${channel}`);
  });
  const defaultYAxisZoomSliderMargin: number = getTempByMeasurement(
    measurement,
    DEFAULT_Y_AXIS_ZOOM_SLIDER_MARGIN_BY_CELSIUS,
  );

  const dataEvent = useRef<DataEvent | null>({});

  const [legendData, setLegendData] = useState({ scrollDataIndex: null, selected: null });
  const [currentYear, setCurrentYear] = useState<number | null>(null);

  const formatYearDayMonthShort = (timestamp) => {
    const date = new Date(timestamp);
    return DateFormatter({ date, format: DateFormats['dd-MMM yyyy'] });
  };

  const getSerIdWithColor = (): { name: string; color: string }[] => {
    const chartInstance = chartRef?.current?.getEchartsInstance();
    if (!chartInstance) return [];

    const series = chartInstance.getOption().series as SeriesOption[];

    return series.reduce((acc, ser) => {
      const isNote = String(ser.name).startsWith(NOTE_ID);

      if (ser.id === SET_POINT_NAME || !ser.color || isNote) return acc;

      const name = ser.id as string;
      const color = (Array.isArray(ser.color) ? ser.color[0] : ser.color) as string;

      acc.push({ name, color });
      return acc;
    }, []);
  };

  /*========================Options===============================*/
  const getVisibleData = ({ startPercent, endPercent }: ParameterGetVisibleDataFn) => {
    const dataLength = cycleComponents.source.length;
    if (dataLength === 0) {
      return [];
    }
    const startIndex = Math.floor((startPercent / 100) * dataLength);
    const endIndex = Math.floor((endPercent / 100) * dataLength);
    const slicedData = cycleComponents.source.slice(startIndex, endIndex);
    const firstPoint = slicedData[0];
    const lastPoint = slicedData.at(-1);

    const res = Math.abs(lastPoint.time - firstPoint.time);

    if (res > TIME.DAY) {
      return getLttbData(slicedData.length, slicedData);
    }

    return slicedData;
  };

  const generateTemperatureArray = (convertedMinTemp, convertedMaxTemp, temperature_grid_size, currentDataZoom) => {
    const result = [];

    const yZoomData = Array.isArray(currentDataZoom)
      ? currentDataZoom?.find((item) => item.id === DATA_ZOOM_ID.SLIDER_Y)
      : null;

    const minTemp = (yZoomData && yZoomData.startValue) || convertedMinTemp;
    const maxTemp = (yZoomData && yZoomData.endValue) || convertedMaxTemp;

    result.push(Math.ceil(minTemp));

    let start = Math.ceil(minTemp / temperature_grid_size) * temperature_grid_size;
    if (start < minTemp) start += temperature_grid_size;

    for (let temp = start; temp <= maxTemp; temp += temperature_grid_size) {
      result.push(temp || 0);
    }

    if (result[result.length - 1] !== maxTemp) {
      result.push(Math.floor(maxTemp));
    }

    return result;
  };

  const getOptions = ({ deviceSeriesData, noteSeriesData, currentDataZoom }: ParameterGetOptionFn): EChartsOption => {
    const cycleMeasurementSymbol =
      measurement === CycleMeasurement.Celsius ? CycleMeasurementSymbol.Celsius : CycleMeasurementSymbol.Fahrenheit;

    const minTemp = chartPreference.min_temperature;
    const maxTemp = chartPreference.max_temperature;

    const convertedMinTemp = getTempByMeasurement(measurement, minTemp);
    const convertedMaxTemp = getTempByMeasurement(measurement, maxTemp);

    const adjustedStartZoomY = convertedMinTemp - defaultYAxisZoomSliderMargin;
    const adjustedEndZoomY = convertedMaxTemp + defaultYAxisZoomSliderMargin;

    const clampedStartZoomY = adjustedStartZoomY < convertedMinTemp ? convertedMinTemp : adjustedStartZoomY;
    const clampedEndZoomY = adjustedEndZoomY > convertedMaxTemp ? convertedMaxTemp : adjustedEndZoomY;

    const dataZoom = currentDataZoom ? currentDataZoom : getDefaultDataZoom(clampedStartZoomY, clampedEndZoomY);
    const currentSliderXOptions = dataZoom.find((el) => el.id === DATA_ZOOM_ID.SLIDER_X);

    const visibleDataSource = getVisibleData({
      startPercent: currentSliderXOptions.start,
      endPercent: currentSliderXOptions.end,
    });
    
    let max: number;
    let min: number;
    // const max: number = chartPreference.end_timestamp ?? new Date().getTime();
    // const min: number = chartPreference.start_timestamp;

    const deviceSeriesOptions: Record<string, LineSeriesOption> = {};
    const spSeriesOptions: SeriesOption = {
      name: SET_POINT_NAME,
      id: SET_POINT_NAME,
      type: 'line',
      symbol: 'none',
      showAllSymbol: false,
      showSymbol: false,
      clip: true,
      connectNulls: true,
      data: [],
      label: { show: false },
      endLabel: { show: false },
      labelLine: { show: false },
      color: '#0000FF',
      lineStyle: {
        color: '#0000FF',
        type: 'dashed',
      },
      emphasis: { disabled: true },
      select: { disabled: true },
      animation: false,
      silent: true,
    };

    for (const d of visibleDataSource) {
      const time = d.time;
      if (d.sp !== undefined) {
        const spItem: [number, number] = [time, d.sp];
        spSeriesOptions.data.push(spItem);
      }
      for (let i = 0; i < deviceSeriesData.length; i++) {
        const index = i;
        const item = deviceSeriesData[index];

        max = visibleDataSource[0].time;
        min = max;

        if (time > max) {
          max = time;
        }
        if (time < min) {
          min = time;
        }

        const temp = d[item.id];

        if (!temp) {
          continue;
        }
        const [controllerSerialNumber, device] = String(item.id).split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
        let customizedColor: string | undefined;

        if (colorsDevices?.length) {
          const match = colorsDevices.find((cd) => cd.controller_serial_number === controllerSerialNumber);
          if (match && (match.channelColors.length > 0 || match.thermocoupleColors.length > 0)) {
            const allColors = [...match.channelColors, ...match.thermocoupleColors];
            const deviceColorEntry = allColors.find((entry) => Object.keys(entry)[0] === device);
            customizedColor = deviceColorEntry ? Object.values(deviceColorEntry)[0] : undefined;
          }
        }

        const color = customizedColor ?? getColorByDeviceIndex(index, chartPreference.colors);

        const deviceItem: [number, number] = [time, temp];

        const option: SeriesOption = {
          name: item.name,
          id: item.id,
          type: 'line',
          symbol: 'none',
          showAllSymbol: false,
          showSymbol: false,
          clip: true,
          connectNulls: true,
          color,
          data: [] as [number, number][],
          xAxisIndex: 1,
          label: { show: false },
          endLabel: { show: false },
          labelLine: { show: false },
          lineStyle: { color },
          emphasis: { disabled: true },
          select: { disabled: true },
          animation: false,
          silent: true,
        };

        const isHide = channelsToHide?.includes(String(item.name));
        if (!isHide) {
          if (!deviceSeriesOptions[item.id]) {
            deviceSeriesOptions[item.id] = option;
          }

          deviceSeriesOptions[item.id].data.push(deviceItem);
        }
      }
    }

    const noteSeries: LineSeriesOption[] = noteSeriesData.map((item, index) => {
      const defaultLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}`,
        fontSize: 14,
        borderWidth: 1,
        width: 48,
        lineHeight: 16,
        padding: [0, 4],
        align: 'center' as const,
        borderRadius: 0,
        backgroundColor: 'transparent',
        overflow: 'truncate' as const,
      };

      const noteViewLabel = {
        show: true,
        position: 'top' as const,
        formatter: `#${index + 1}\n${item.name}`,
        fontSize: 16,
        color: '#000',
        borderColor: '#000',
        borderType: 'solid' as const,
        borderWidth: 1,
        width: 110,
        lineHeight: 24,
        padding: 20,
        borderRadius: 6,
        backgroundColor: 'white',
        overflow: 'truncate' as const,
      };

      const label = isNoteView ? noteViewLabel : defaultLabel;

      const markPointData = [
        {
          name: 'coordinate',
          occurrenceId: 'root',
          coord: item.media?.coord,
          label,
        },
        ...(item.links ?? []).map((link) => ({
          name: 'coordinate',
          occurrenceId: link.id,
          coord: link.coord,
          label,
        })),
      ];

      const noteSerOption: SeriesOption = {
        id: item.id,
        name: generateNoteName(item.name),
        encode: { x: 'time', y: item.id },
        type: 'line',
        connectNulls: false,
        clip: true,
        tooltip: {
          show: false,
        },
        symbol: 'none',
        lineStyle: {
          opacity: 0,
        },
        markPoint: {
          symbol: 'diamond',
          symbolSize: 12,
          itemStyle: {
            color: '#EF8541',
          },
          data: markPointData,
        },
      };

      return noteSerOption;
    });

    const series: LineSeriesOption[] = Object.values(deviceSeriesOptions);
    series.push(...noteSeries, spSeriesOptions);

    const customValues = generateTemperatureArray(
      convertedMinTemp,
      convertedMaxTemp,
      chartPreference.temperature_grid_size,
      currentDataZoom,
    );

    const yAxis: YAXisComponentOption = {
      type: 'value',
      scale: true,
      min: convertedMinTemp,
      max: convertedMaxTemp,
      interval: chartPreference.temperature_grid_size,
      splitLine: {
        show: true,
        lineStyle: {
          color: '#e0e0e0',
        },
      },
      axisLabel: {
        formatter: (value) => `${value} ${cycleMeasurementSymbol}`,
        color: '#000',
        customValues,
      },
      axisTick: {
        show: true,
        customValues,
      },
      triggerEvent: false,
    };

    if (chartPreference.isDefaultTemplate) {
      yAxis.min = (value) => applyYAxisPadding(value.min, { min: convertedMinTemp, max: convertedMaxTemp }, 'min');
      yAxis.max = (value) => applyYAxisPadding(value.max, { min: convertedMinTemp, max: convertedMaxTemp }, 'max');

      yAxis.axisLabel = {
        ...yAxis.axisLabel,
        formatter: (value) => `${value}`,
      };

      delete yAxis.interval;
    }

    const chartOptions: EChartsOption = {
      tooltip: [
        {
          id: 'one',
          trigger: 'axis',
          position(point, _, __, ___, size) {
            const { contentSize, viewSize } = size;
            const [x, y] = point;
            const [contentWidth, contentHeight] = contentSize;
            const [viewWidth, viewHeight] = viewSize;

            const moveLeft = x + contentWidth >= viewWidth;
            const moveUp = y + contentHeight >= viewHeight;

            const adjustedX = moveLeft ? x - contentWidth : x;
            const adjustedY = moveUp ? y - contentHeight : y;

            return [adjustedX, adjustedY];
          },
          formatter: function (params) {
            const date = DateFormatter({
              date: new Date(params[0].axisValueLabel),
              format: DateFormats['dd-MMM yyyy HH:mm:ss a'],
            });
            let tooltipContent = `${date}<br/>`;

            params.forEach((item) => {
              const seriesName = item.seriesName;
              const value = item.data.at(1);
              const data = value ? `${value} ${cycleMeasurementSymbol}` : '';

              if (seriesName === SET_POINT_NAME) {
                tooltipContent += `${item.marker} ${SET_POINT_NAME} (${t('iot.setpoint')}): ${data}<br/>`;
                return;
              }

              const seriesEntity = item.seriesName.split(CHARACTER_SEPARATOR_IOT_KEY_NAME);
              if (seriesEntity.length < 2) {
                tooltipContent += `${item.marker} ${seriesName}: ${data}<br/>`;
                return;
              }
              const controllerSerialNumber = seriesEntity[0];
              const deviceNameData = seriesEntity[1].split(CHARACTER_SEPARATOR_DEVICE_NAME);
              const deviceType = deviceNameData[0];
              const deviceNumber = deviceNameData[1];
              const formattedControllerName = cycleControllersName[controllerSerialNumber];
              const formattedDevice = DEVICE_NAME[deviceType] + CHARACTER_SEPARATOR_DEVICE_NAME + deviceNumber;

              const finishedDeviceName = nameDevices
                ? nameDevices[controllerSerialNumber + CHARACTER_SEPARATOR_IOT_KEY_NAME + seriesEntity[1]] ||
                formattedDevice
                : formattedDevice;
              tooltipContent += `${item.marker} ${finishedDeviceName}\t(${formattedControllerName}): ${data} <br/>`;
            });

            return tooltipContent;
          },
        },
      ],
      legend: {
        show: false,
      },
      grid: {
        show: true,
        top: `${CHART_INDENTS.GRID.TOP}px`,
        left: `${CHART_INDENTS.GRID.LEFT}px`,
        right: `${CHART_INDENTS.GRID.RIGHT}px`,
        bottom: `${CHART_INDENTS.GRID.BOTTOM}px`,
        borderColor: '#000',
      },
      xAxis: [
        {
          type: 'time',
          min,
          max,
          position: 'bottom',
          axisTick: {
            customValues: generateTimeAxisValues(min, max, chartPreference.time_grid_side),
          },
          axisLabel: {
            ...AXIS_CONFIG.TIME.BOTTOM,
            customValues: generateTimeAxisValues(min, max, chartPreference.time_grid_side),
          },
          splitLine: AXIS_CONFIG.SPLIT_LINE,
        },
        {
          type: 'time',
          min,
          max,
          position: 'top',
          axisTick: {
            customValues: generateTimeAxisValues(min, max, chartPreference.time_grid_side),
          },
          axisLabel: {
            show: true,
            ...AXIS_CONFIG.TIME.TOP,
            customValues: generateTimeAxisValues(min, max, chartPreference.time_grid_side),
          },
        },
      ],
      yAxis,
      animationDurationUpdate: 0,
      dataZoom,
      series,
    };

    return chartOptions;
  };

  const generateCycle = (cycleComponents: CycleComponents) => {
    const chartInstance = chartRef.current?.getEchartsInstance() ?? null;
    const dataZoom = chartInstance ? chartInstance.getOption().dataZoom : null;

    const chartOptions = getOptions({ ...cycleComponents, currentDataZoom: dataZoom as DataZoomComponentOption[] });

    return chartOptions;
  };
  /*========================Options===============================*/

  const checkChartZone = (event: any) => {
    const chartContainer = document.querySelector('.c-cycle-chart-preview__container') as HTMLElement;
    const chartElement = document.querySelector('.c-cycle-chart-preview__inner') as HTMLElement;

    const rect = chartContainer.getBoundingClientRect();
    const chartElementRect = chartElement.getBoundingClientRect();

    const topPart = window.getComputedStyle(chartContainer, '::before');
    const bottomPart = window.getComputedStyle(chartContainer, '::after');
    const leftPart = window.getComputedStyle(chartElement, '::before');
    const rightChartElement = window.getComputedStyle(chartElement, '::after');

    if (leftPart && event.offsetX <= CHART_INDENTS.GRID.LEFT) {
      return false;
    }

    if (rightChartElement && event.clientX >= rect.right - CHART_INDENTS.GRID.RIGHT) {
      return false;
    }

    if (topPart && event.offsetY <= CHART_INDENTS.GRID.TOP) {
      return false;
    }

    if (bottomPart && event.clientY >= chartElementRect.bottom - CHART_INDENTS.GRID.BOTTOM) {
      return false;
    }

    return true;
  };

  const areObjectsClose = (coordinateOne: Coordinate, coordinateTwo: Coordinate, tolerance: number = 1) =>
    Math.abs(coordinateOne.x - coordinateTwo.x) <= tolerance &&
    Math.abs(coordinateOne.y - coordinateTwo.y) <= tolerance;

  const viewCycleCreation = (event: any, chart: any) => {
    const isClickZone = checkChartZone(event);

    const currentDataEvent = { ...dataEvent.current };
    dataEvent.current = null;

    if (!isClickZone) {
      return;
    }

    const isCurrentPoint = areObjectsClose(
      {
        x: currentDataEvent.pointerdown.offsetX,
        y: currentDataEvent.pointerdown.offsetY,
      },
      {
        x: event.offsetX,
        y: event.offsetY,
      },
    );

    if (
      (currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_X ||
        currentDataEvent.zoom?.dataZoomId === DATA_ZOOM_ID.SLIDER_Y) &&
      !isCurrentPoint
    ) {
      return false;
    }

    if (Array.isArray(currentDataEvent.zoom) && !isCurrentPoint) {
      return false;
    }

    createNote(event, chart);
  };

  const updateVisibleData = () => {
    const chartInstance = chartRef.current.getEchartsInstance();

    const dataZoom = chartInstance.getOption().dataZoom;

    const options = getOptions({ ...cycleComponents, currentDataZoom: dataZoom as DataZoomComponentOption[] });

    chartInstance.setOption(options as any, false);
  };

  const handleDataZoom = (chartEvent, chart) => {
    if (chartEvent.dataZoomId === DATA_ZOOM_ID.SLIDER_X) {
      const time = chart?.getOption()?.series[0]?.data?.[0]?.[0] ?? new Date().getFullYear();
      setCurrentYear(time);
    }
    if (chartEvent.dataZoomId === DATA_ZOOM_ID.SLIDER_X || chartEvent.batch?.length) {
      let refetch = false;
      if (chartEvent.dataZoomId === DATA_ZOOM_ID.SLIDER_X) {
        refetch = shouldRefetch(chartEvent.start, chartEvent.end);
      }
      if (chartEvent.batch?.length) {
        const zoom = chartEvent.batch.find((ch) => ch.dataZoomId === DATA_ZOOM_ID.INSIDE_X);
        refetch = shouldRefetch(zoom?.start ?? 0, zoom?.end ?? 100);
      }

      if (refetch) {
        updateVisibleData();
      }
    }
  };

  const updateDataEvent = (data: any, name: keyof DataEvent) => {
    dataEvent.current = { ...dataEvent.current, [name]: data };
  };

  const onChartReady = (chart: EChartsInstance) => {
    if (currentYear === null) {
      const time = chart?.getOption()?.series[0]?.data?.[0]?.[0] ?? new Date().getFullYear();
      setCurrentYear(time);
    }

    chart.on('click', editNote);
    chart.on('dataZoom', (event) => {
      chart.dispatchAction({ type: 'hideTip' });
      handleDataZoom(event, chart);
      chart.dispatchAction({ type: 'showTip' });
    });
    chart.getDom().addEventListener('pointerdown', (event) => updateDataEvent(event, 'pointerdown'));
    chart.getDom().addEventListener('pointermove', (event) => updateDataEvent(event, 'pointermove'));
    chart.getDom().addEventListener('click', (event) => viewCycleCreation(event, chart));
    chart.on('legendselectchanged', (params) => {
      setLegendData((oldValue) => ({ ...oldValue, selected: params.selected }));
    });
    chart.on('legendScroll', (params) => {
      setLegendData((oldValue) => ({ ...oldValue, scrollDataIndex: params.scrollDataIndex }));
    });
  };

  const getOptionsData = () => {
    const currentOptions = generateCycle(cycleComponents);

    if (chartRef.current) {
      if (legendData.scrollDataIndex !== null) {
        currentOptions.legend = { ...currentOptions.legend, scrollDataIndex: legendData.scrollDataIndex };
      }

      if (legendData.selected !== null) {
        currentOptions.legend = { ...currentOptions.legend, selected: legendData.selected };
      }
    }

    return currentOptions;
  };

  return (
    <>
      <div className="c-cycle-chart-preview relative z-0 h-full">
        <div
          className={`c-cycle-chart-preview__container h-full flex-auto ${CLASS_NAME_SPACE.BOTTOM} ${CLASS_NAME_SPACE.TOP}`}
        >
          <div className={`c-cycle-chart-preview__inner h-full ${CLASS_NAME_SPACE.LEFT} ${CLASS_NAME_SPACE.RIGHT}`}>
            <span className="relative left-[6px] top-[42px] text-[12px] font-semibold">
              {chartPreference.start_timestamp && formatYearDayMonthShort(Number(chartPreference.start_timestamp))}
            </span>
            <ReactECharts
              onChartReady={onChartReady}
              ref={chartRef}
              option={getOptionsData()}
              style={{ height: '100%', width: '100%' }}
              className="c-cycle-chart-preview__chart echart"
              showLoading={isLoading}
              loadingOption={{
                text: t('labels.loading'),
                color: '#EF8541',
              }}
            />
          </div>
        </div>
      </div>

      {chartRef?.current && !!channelsToHide && (
        <ListLegendsCycle
          list={getSerIdWithColor()}
          cycleControllersName={cycleControllersName}
          nameDevices={nameDevices}
          channelsToHide={channelsToHide}
        />
      )}
    </>
  );
};
